import { SocialLoginComponent } from './../social.login.component';
import { Component, EventEmitter, Output } from '@angular/core';
import { ConfigService } from 'src/app/common/services/config/config.service';
import { CheckLoginStatusService } from '../checkLoginStatus.service';

@Component({
  selector: 'ig-facebook-login',
  templateUrl: './facebook.login.component.html',
  styleUrls: ['./facebook.login.component.scss'],
})
export class FacebookLoginComponent {
  public static isUserLoggedIn: boolean;
  public static authResponse: any;
  public static userAccessToken: string;
  public static profilePicture: string;
  public static userFacebookId: string;

  @Output() public onLoginStatusChanged = new EventEmitter<boolean>();

  constructor(private checkLoginStatusService: CheckLoginStatusService) {
    // Only initialize FB SDK if not in webview (app context)
    if (!this.isInWebView()) {
      setTimeout(() => {
        this.initFB();
        this.syncFacebookStatus();
      }, 3000);
    }
  }

  login() {
    // If in webview, request app-level login
    if (this.isInWebView()) {
      this.requestAppLogin();
    }

    // Regular FB SDK login for non-webview environments
    if (!window['FB']) {
      console.warn('Facebook SDK not loaded');
      return;
    }

    window['FB'].login(
      (response: any) => {
        console.log('FB login response', response);

        if (!response || !response['authResponse']) {
          return;
        }

        this.syncFacebookStatus();
      },
      { scope: ConfigService.config.facebook.graphApi.scope }
    );
  }

  public get isUserLoggedWithFB() {
    return FacebookLoginComponent.isUserLoggedIn;
  }

  public get profilePicture() {
    return FacebookLoginComponent.profilePicture;
  }

  public logout() {
    // If in webview, request app-level logout
    if (this.isInWebView()) {
      this.requestAppLogout();
      FacebookLoginComponent.clearLoginData();
      return;
    }

    // Regular FB SDK logout for non-webview environments
    if (!window['FB']) {
      console.warn('Facebook SDK not loaded');
      return;
    }

    window['FB'].logout(() => {
      this.syncFacebookStatus();
    });
  }

  // Static method to update Facebook login status from app-level OAuth
  public static updateFromAppLogin(loginData: any) {
    if (loginData.isLoggedIn && loginData.provider === 'facebook') {
      FacebookLoginComponent.isUserLoggedIn = true;
      FacebookLoginComponent.userFacebookId = loginData.userId;
      FacebookLoginComponent.userAccessToken = loginData.accessToken;
      FacebookLoginComponent.profilePicture = loginData.profilePicture;
      FacebookLoginComponent.authResponse = {
        authResponse: {
          userID: loginData.userId,
          accessToken: loginData.accessToken,
        },
      };

      // Store in localStorage for compatibility with existing code
      localStorage.setItem(
        'social.login.facebookUser',
        JSON.stringify({
          userId: loginData.userId,
          profilePicture: loginData.profilePicture,
        })
      );

      SocialLoginComponent.loginStatus$.next(true);
    } else {
      FacebookLoginComponent.clearLoginData();
    }
  }

  // Static method to clear Facebook login data
  public static clearLoginData() {
    FacebookLoginComponent.isUserLoggedIn = false;
    FacebookLoginComponent.authResponse = null;
    FacebookLoginComponent.userAccessToken = null;
    FacebookLoginComponent.profilePicture = null;
    FacebookLoginComponent.userFacebookId = null;
    localStorage.removeItem('social.login.facebookUser');
    SocialLoginComponent.loginStatus$.next(false);
  }

  // Check if we're in a webview
  private isInWebView(): boolean {
    return (
      window.parent !== window ||
      navigator.userAgent.includes('wv') || // Android WebView
      (navigator.userAgent.includes('Version/') &&
        navigator.userAgent.includes('Mobile/'))
    ); // iOS WebView
  }

  // Request app-level Facebook login
  private requestAppLogin() {
    const message = {
      type: 'APP_LOGIN_REQUEST',
      provider: 'facebook',
    };

    if (window.parent !== window) {
      window.parent.postMessage(message, '*');
    }
  }

  // Request app-level Facebook logout
  private requestAppLogout() {
    const message = {
      type: 'APP_LOGOUT_REQUEST',
      provider: 'facebook',
    };

    if (window.parent !== window) {
      window.parent.postMessage(message, '*');
    }
  }

  private syncFacebookStatus() {

    try {
      if (!window['FB']) {
        console.warn('Facebook SDK not available');
        return;
      }

      window['FB'].getLoginStatus((response) => {
        console.log('getLoginStatus response', response);

        if (response.status === 'connected') {
          FacebookLoginComponent.isUserLoggedIn = true;
          FacebookLoginComponent.authResponse = response;
          FacebookLoginComponent.userFacebookId = response.authResponse.userID;
          FacebookLoginComponent.userAccessToken =
            response.authResponse.accessToken;
          FacebookLoginComponent.profilePicture =
            ConfigService.config.facebook.graphApi.profilePicture.replace(
              '${userId}',
              response.authResponse.userID
            );

          // Store in localStorage for compatibility
          localStorage.setItem(
            'social.login.facebookUser',
            JSON.stringify({
              userId: response.authResponse.userID,
              profilePicture: FacebookLoginComponent.profilePicture,
            })
          );
          this.checkLoginStatusService.checkFacebookLoginStatus();
          SocialLoginComponent.loginStatus$.next(true);
        } else {
          FacebookLoginComponent.clearLoginData();
        }
      });
    } catch (e) {
      console.warn('Facebook sync error:', e);
    }
  }

  private initFB() {
    // Skip if in webview - app handles Facebook SDK
    if (this.isInWebView()) {
      return;
    }

    try {
      if (window['FB'] && ConfigService.config?.facebook?.sdkConfig) {
        window['FB'].init(ConfigService.config.facebook.sdkConfig);
      }
    } catch (e) {
      console.warn('Facebook init error:', e);
    }
  }

  // Method to check login status on component initialization
  public checkInitialLoginStatus() {
    // Check if there's existing login data in localStorage
    const storedFacebookUser = localStorage.getItem(
      'social.login.facebookUser'
    );
    if (storedFacebookUser) {
      try {
        const userData = JSON.parse(storedFacebookUser);
        FacebookLoginComponent.isUserLoggedIn = true;
        FacebookLoginComponent.userFacebookId = userData.userId;
        FacebookLoginComponent.profilePicture = userData.profilePicture;
        SocialLoginComponent.loginStatus$.next(true);
      } catch (e) {
        console.warn('Error parsing stored Facebook user data:', e);
        localStorage.removeItem('social.login.facebookUser');
      }
    }
  }
}
